// src/services/reviewService.ts
import { supabase } from './supabase';
import {CreateReviewData, ProductRating, ProductReview, VariantRating} from "../types";


class ReviewService {
    private supabase = supabase;

    // Получить отзывы для варианта товара
    async getReviewsByVariant(
        variantId: number,
        page: number = 1,
        limit: number = 10,
        sortBy: 'newest' | 'oldest' | 'highest' | 'lowest' = 'newest'
    ): Promise<{ reviews: ProductReview[]; total: number }> {
        let query = this.supabase
            .from('product_reviews')
            .select(`
                *,
                customer:customers (
                    first_name,
                    last_name
                )
            `, { count: 'exact' })
            .eq('variant_id', variantId)
            .eq('is_approved', true)
            .is('deleted_at', null);

        // Сортировка
        switch (sortBy) {
            case 'newest':
                query = query.order('created_at', { ascending: false });
                break;
            case 'oldest':
                query = query.order('created_at', { ascending: true });
                break;
            case 'highest':
                query = query.order('rating', { ascending: false });
                break;
            case 'lowest':
                query = query.order('rating', { ascending: true });
                break;
        }

        // Пагинация
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;
        return { reviews: data || [], total: count || 0 };
    }

    // Получить отзывы для продукта (без варианта)
    async getReviewsByProduct(
        productId: number,
        page: number = 1,
        limit: number = 10,
        sortBy: 'newest' | 'oldest' | 'highest' | 'lowest' = 'newest'
    ): Promise<{ reviews: ProductReview[]; total: number }> {
        let query = this.supabase
            .from('product_reviews')
            .select(`
                *,
                customer:customers (
                    first_name,
                    last_name
                )
            `, { count: 'exact' })
            .eq('product_id', productId)
            .is('variant_id', null)
            .eq('is_approved', true)
            .is('deleted_at', null);

        switch (sortBy) {
            case 'newest':
                query = query.order('created_at', { ascending: false });
                break;
            case 'oldest':
                query = query.order('created_at', { ascending: true });
                break;
            case 'highest':
                query = query.order('rating', { ascending: false });
                break;
            case 'lowest':
                query = query.order('rating', { ascending: true });
                break;
        }

        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;
        return { reviews: data || [], total: count || 0 };
    }

    // Получить рейтинг для варианта
    async getVariantRating(variantId: number): Promise<VariantRating | null> {
        const { data, error } = await this.supabase
            .from('variant_ratings')
            .select('*')
            .eq('variant_id', variantId)
            .maybeSingle();

        if (error) throw error;
        return data;
    }

    // Получить рейтинг для продукта
    async getProductRating(productId: number): Promise<ProductRating | null> {
        const { data, error } = await this.supabase
            .from('product_ratings')
            .select('*')
            .eq('product_id', productId)
            .maybeSingle();

        if (error) throw error;
        return data;
    }

    // Создать отзыв
    async createReview(reviewData: CreateReviewData, customerId: string): Promise<ProductReview> {
        // Проверяем, покупал ли пользователь этот товар
        const { data: purchaseCheck, error: purchaseError } = await this.supabase
            .rpc('check_user_purchased_product', {
                p_customer_id: customerId,
                p_product_id: reviewData.product_id
            });

        if (purchaseError) console.error('Purchase check error:', purchaseError);

        const { data, error } = await this.supabase
            .from('product_reviews')
            .insert({
                ...reviewData,
                customer_id: customerId,
                is_verified_purchase: !!purchaseCheck
            })
            .select(`
                *,
                customer:customers (
                    first_name,
                    last_name
                )
            `)
            .single();

        if (error) throw error;
        return data;
    }

    // Обновить отзыв
    // Убедитесь, что эти методы есть в reviewService.ts:

// Обновить отзыв
    async updateReview(reviewId: string, updates: Partial<CreateReviewData>): Promise<ProductReview> {
        const { data, error } = await this.supabase
            .from('product_reviews')
            .update({
                ...updates,
                updated_at: new Date().toISOString()
            })
            .eq('id', reviewId)
            .select(`
            *,
            customer:customers (
                first_name,
                last_name
            )
        `)
            .single();

        if (error) throw error;
        return data;
    }

// Удалить отзыв (мягкое удаление)
    async deleteReview(reviewId: string): Promise<void> {
        const { error } = await this.supabase
            .from('product_reviews')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', reviewId);

        if (error) throw error;
    }

    // Отметить отзыв как полезный
    async markHelpful(reviewId: string, customerId: string): Promise<void> {
        const { error } = await this.supabase.rpc('mark_review_helpful', {
            p_review_id: reviewId,
            p_customer_id: customerId
        });

        if (error) throw error;
    }

    // Проверить, отметил ли пользователь отзыв как полезный
    async isReviewHelpfulByUser(reviewId: string, customerId: string): Promise<boolean> {
        const { data, error } = await this.supabase
            .from('review_helpful_votes')
            .select('id')
            .eq('review_id', reviewId)
            .eq('customer_id', customerId)
            .maybeSingle();

        if (error) throw error;
        return !!data;
    }

    // Получить статистику отзывов для варианта
    async getReviewStats(variantId: number): Promise<{
        average: number;
        total: number;
        distribution: { [key: number]: number };
    }> {
        const rating = await this.getVariantRating(variantId);

        if (!rating) {
            return {
                average: 0,
                total: 0,
                distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
            };
        }

        return {
            average: rating.average_rating,
            total: rating.total_reviews,
            distribution: {
                1: rating.rating_1_count,
                2: rating.rating_2_count,
                3: rating.rating_3_count,
                4: rating.rating_4_count,
                5: rating.rating_5_count
            }
        };
    }

    // Получить статистику отзывов для продукта
    async getProductReviewStats(productId: number): Promise<{
        average: number;
        total: number;
        distribution: { [key: number]: number };
    }> {
        const rating = await this.getProductRating(productId);

        if (!rating) {
            return {
                average: 0,
                total: 0,
                distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
            };
        }

        return {
            average: rating.average_rating,
            total: rating.total_reviews,
            distribution: {
                1: rating.rating_1_count,
                2: rating.rating_2_count,
                3: rating.rating_3_count,
                4: rating.rating_4_count,
                5: rating.rating_5_count
            }
        };
    }
}

export const reviewService = new ReviewService();